import { type ComponentProps, useEffect, useRef, useState } from "react";

import { checkSkillName } from "@/api/skills";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface SkillRenameDialogProps {
    open: boolean;
    skillId: number;
    currentName: string;
    maxLength: number;
    onOpenChange: (open: boolean) => void;
    onRename: (name: string) => void;
    finalFocus?: ComponentProps<typeof DialogContent>["finalFocus"];
}

export function SkillRenameDialog({
    open,
    skillId,
    currentName,
    maxLength,
    onOpenChange,
    onRename,
    finalFocus,
}: SkillRenameDialogProps) {
    const [name, setName] = useState(currentName);
    const [error, setError] = useState<string | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const requestRef = useRef(0);
    const skillIdRef = useRef(skillId);
    const currentNameRef = useRef(currentName);

    useEffect(() => {
        skillIdRef.current = skillId;
        currentNameRef.current = currentName;
    });

    useEffect(() => {
        requestRef.current += 1;
    }, [skillId]);

    useEffect(() => {
        if (!open) return;
        requestRef.current += 1;
        setName(currentNameRef.current);
        setError(null);
        setIsChecking(false);
    }, [open]);

    const submit = async () => {
        const targetSkillId = skillIdRef.current;
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError("Enter a skill name");
            return;
        }
        if (trimmedName === currentNameRef.current.trim()) {
            onOpenChange(false);
            return;
        }
        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        setIsChecking(true);
        setError(null);
        try {
            const { available } = await checkSkillName(
                trimmedName,
                targetSkillId
            );
            if (
                requestRef.current !== requestId ||
                skillIdRef.current !== targetSkillId
            ) {
                return;
            }
            if (!available) {
                setError("A skill with this name already exists");
                return;
            }
            onRename(trimmedName);
            onOpenChange(false);
        } catch {
            if (requestRef.current === requestId) {
                setError("Failed to check this name. Try again.");
            }
        } finally {
            if (requestRef.current === requestId) {
                setIsChecking(false);
            }
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next && isChecking) return;
                onOpenChange(next);
            }}
        >
            <DialogContent className="sm:max-w-md" finalFocus={finalFocus}>
                <DialogHeader>
                    <DialogTitle>Rename skill</DialogTitle>
                    <DialogDescription>
                        Change the display name. Save the draft to persist the
                        rename.
                    </DialogDescription>
                </DialogHeader>
                <form
                    className="space-y-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        void submit();
                    }}
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="skill-rename">Skill name</Label>
                        <Input
                            id="skill-rename"
                            value={name}
                            onChange={(event) => {
                                requestRef.current += 1;
                                setName(event.target.value.slice(0, maxLength));
                                setError(null);
                            }}
                            maxLength={maxLength}
                            autoFocus
                            disabled={isChecking}
                            aria-invalid={Boolean(error)}
                            aria-describedby={
                                error
                                    ? "skill-rename-error"
                                    : "skill-rename-count"
                            }
                        />
                        {error ? (
                            <p
                                id="skill-rename-error"
                                role="alert"
                                className="text-destructive text-sm"
                            >
                                {error}
                            </p>
                        ) : (
                            <p
                                id="skill-rename-count"
                                className={cn(
                                    "text-xs",
                                    name.length >= maxLength
                                        ? "text-destructive"
                                        : "text-muted-foreground"
                                )}
                            >
                                {name.length}/{maxLength}
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isChecking}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isChecking || name.trim() === ""}
                        >
                            {isChecking ? "Checking…" : "Rename"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
